import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { useEscape } from '@/shared/hooks/useEscape';
import { useFocusTrap } from '@/shared/hooks/useFocusTrap';
import { useScrollLock } from '@/shared/hooks/useScrollLock';
import { COPY } from '@/shared/content/copy';
import styles from './ls-overlay.module.scss';

/**
 * Где лежит окно внутри модуля.
 *
 * `bottom` — окно снизу, во всю ширину: так на телефоне открываются
 * размеры. Снизу потому, что рука держит телефон за низ, и содержимое
 * окна оказывается под большим пальцем, а не под шапкой.
 */
type Align = 'center' | 'start' | 'end' | 'bottom';

interface Props {
  align?: Align;
  label: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * Общая механика всплывающих слоёв: подложка с размытием, закрытие по Escape
 * и клику мимо, удержание фокуса.
 *
 * Слой ограничен модулем, а не окном: виджет встраивается на чужую страницу,
 * и затемнять её навигацию мы не имеем права.
 *
 * Рисуется порталом в корень виджета, а не там, где вызван. Экраны лежат
 * в области, которая обрезает содержимое по границе модуля, — и окно,
 * нарисованное внутри неё, обрезалось вместе с экраном. Портал поднимает
 * его на уровень оболочки, где обрезать уже нечему.
 */
export function LsOverlay({ align = 'center', label, onClose, children }: Props) {
  useEscape(onClose);
  useScrollLock();
  const trapRef = useFocusTrap<HTMLDivElement>();

  const root = document.getElementById('ls-root');
  if (!root) return null;

  return createPortal(
    <div className={`${styles['ls-overlay']} ${styles[`ls-overlay--align-${align}`]}`}>
      <button
        className={styles['ls-overlay__backdrop']}
        type="button"
        onClick={onClose}
        aria-label={COPY.cart.close}
      />

      <div className={styles['ls-overlay__body']} ref={trapRef} role="dialog" aria-label={label}>
        {children}
      </div>
    </div>,
    root,
  );
}
